import type { Capitolo } from '../tipi.ts'

const capitolo: Capitolo = {
  id: 'percorsi-interventi',
  titolo: 'Percorsi, interventi e ricontatti',
  gruppo: 'Il lavoro con le persone',
  permesso: ['intervento.crea', 'follow_up.gestisci'],
  aCheServe: 'Il **percorso** è la presa in carico della persona su un tipo di intervento. L’**intervento** è la '
    + 'singola prestazione: un colloquio, un accompagnamento, una mediazione. Il **ricontatto** è un '
    + 'appuntamento preso per la persona, o il giorno in cui risentirla.',
  parti: [
    {
      titolo: 'Registrare un intervento',
      permesso: 'intervento.crea',
      passi: [
        'In testa alla scheda premi «Registra intervento». Per un percorso nuovo puoi anche usare «Apri un '
          + 'percorso» nella linguetta «Percorsi»: un percorso nasce sempre con il suo primo intervento.',
        'In «Percorso e tipologia» scegli il percorso aperto, oppure «— nuovo percorso —» con l’«Area» e la '
          + '«Tipologia di intervento». Se prevista, scegli la «Sottotipologia».',
        'In «L’intervento» compila data, modalità, sede, comune, categoria di utenti, ente coinvolto e progetto. La '
          + '«Durata» è facoltativa: «Ore» e «Minuti» (0, 15, 30 o 45), anche uno solo dei due.',
        'In «Progetto» trovi, in ordine alfabetico, i progetti della sede scelta nel modulo (con i suoi sportelli). '
          + '«Mostra tutti i progetti» mostra anche quelli delle altre sedi e quelli senza sede. Se la sede non ha '
          + 'ancora progetti collegati, li trovi tutti. Se l’intervento è '
          + 'dell’attività istituzionale, spunta «Attività istituzionale (nessun progetto)».',
        'In «Esito e note» scrivi che cosa hai fatto.',
        'Premi «Salva». Se ti manca un dato, premi «Salva come bozza» e completa dopo.',
      ],
      immagine: {
        file: 'registra-intervento.webp', larghezza: 1200, altezza: 760,
        descrizione: 'La pagina Registra intervento con i riquadri Percorso e tipologia e L’intervento.',
      },
      nota: 'Se lasci la pagina con modifiche non salvate, la finestra «Modifiche non salvate» chiede «Salva» o '
        + '«Non salvare». Cambiando sede l’elenco dei progetti cambia; il progetto già scelto resta, con una nota se '
        + 'non è della sede. Il campo «Tema» non c’è più dal 4/11/2026: il tema degli interventi registrati prima si '
        + 'legge aprendo l’intervento, e la modifica non lo tocca.',
    },
    {
      titolo: 'Completare le bozze',
      permesso: 'intervento.crea',
      passi: [
        'Apri «Interventi» dal menu, oppure «I tuoi interventi in bozza» nel Cruscotto.',
        'Scegli lo stato «Da completare» e spunta «Solo i miei».',
        'Premi «Completa» nella riga, compila i dati che mancano e premi «Salva».',
      ],
      immagine: {
        file: 'interventi-bozze.webp', larghezza: 1200, altezza: 760,
        descrizione: 'La pagina Interventi filtrata sulle bozze da completare, con il comando Completa.',
      },
      nota: 'Le mediazioni svolte creano da sole un intervento in bozza a nome di chi le ha programmate: si '
        + 'completa allo stesso modo.',
    },
    {
      titolo: 'Chiudere un percorso',
      permesso: 'percorso.gestisci',
      passi: [
        'Nella linguetta «Percorsi» premi «Apri» sul percorso, poi «Chiudi percorso».',
        'Scrivi il «Motivo della chiusura» e la «Data di chiusura», poi premi «Chiudi il percorso».',
      ],
      nota: 'Un percorso con interventi in bozza non si chiude: completali prima. Un percorso chiuso non si riapre.',
    },
    {
      titolo: 'Fissare un ricontatto',
      permesso: 'follow_up.gestisci',
      passi: [
        'In testa alla scheda premi «Fissa un ricontatto». Mentre registri un intervento nuovo, puoi invece '
          + 'spuntare «Fissa anche un ricontatto».',
        'Compila «Giorno», «Ora» (facoltativa) e «Motivo», per esempio «appuntamento con l’avvocato».',
        'In «Chi lo segue» resti tu, oppure scegli un collega: riceve un avviso.',
        'Premi «Fissa il ricontatto».',
      ],
      immagine: {
        file: 'ricontatti.webp', larghezza: 1200, altezza: 760,
        descrizione: 'La linguetta Percorsi con il riquadro Ricontatti e appuntamenti e l’elenco dei percorsi.',
      },
      nota: 'Due giorni prima arriva il promemoria «Ricontatto il …». Il motivo non compare negli avvisi: si legge '
        + 'nella scheda. Per un incontro in sede, con durata e stanza, usa «Fissa un appuntamento»: compare '
        + 'nell’agenda della sede (capitolo «Agenda della sede, orari e turni»).',
    },
    {
      titolo: '«Com’è andato?»',
      permesso: 'follow_up.gestisci',
      passi: [
        'Dopo l’appuntamento, sul ricontatto premi «Com’è andato?».',
        'Scegli l’«Esito»: «Fatto», «La persona non si è presentata» oppure «Annullato».',
        'Se vuoi, scrivi una «Nota», poi premi «Registra».',
      ],
      nota: 'Per spostare un appuntamento usa «Modifica» finché il ricontatto è aperto. Un ricontatto chiuso non '
        + 'si modifica più.',
    },
    {
      titolo: 'La pagina «Ricontatti»',
      permesso: 'follow_up.gestisci',
      testo: [
        'La voce di menu «Ricontatti» elenca gli appuntamenti. Con «Quali» scegli «In programma», «Fatti», «Rimasti '
          + 'senza esito» e gli altri; con «Di chi» scegli «Quelli che seguo io» o «Tutti quelli che posso vedere».',
        'Un ricontatto passato senza esito diventa «da chiudere»; dopo trenta giorni «senza esito». Si può '
          + 'ancora chiudere, oppure spostare con «Modifica».',
      ],
    },
  ],
  avvertenze: [
    {
      quando: '«Per «Salva» mancano i dati elencati qui sotto…»',
      cosaFare: 'Completa i campi indicati (comune, categoria, fascia d’età, ente coinvolto, progetto), oppure '
        + 'premi «Salva come bozza» e completa dopo.',
    },
    {
      quando: 'Il progetto che cerchi non è nell’elenco.',
      cosaFare: 'L’elenco parte dai progetti della sede scelta nel modulo: premi «Mostra tutti i progetti». Se il '
        + 'progetto non ha ancora una sede, chiedi a chi gestisce i progetti di collegarlo alla sua.',
    },
    {
      quando: '«Durata: ore intere da 0 a 24, minuti 0, 15, 30 o 45.»',
      cosaFare: 'Nelle «Ore» scrivi un numero intero, senza virgola: un’ora e mezza è 1 ora e 30 minuti.',
    },
    {
      quando: '«Con i tuoi incarichi non puoi aprire un percorso nella sede «…».»',
      cosaFare: 'Il messaggio dice quali sedi puoi usare: scegline una nel campo «Sede». Se la persona va seguita da '
        + 'un’altra sede, chiedi a un collega di quella sede.',
    },
    {
      quando: '«Con i tuoi incarichi non puoi registrare interventi su questo percorso, che è della sede «…».»',
      cosaFare: 'Scegli «— nuovo percorso —» e apri un percorso nella tua sede.',
    },
    {
      quando: 'Le «Ore di mediazione» non vengono accettate.',
      cosaFare: 'Si scrivono a mezz’ore, da 0,5 a 10.',
    },
    {
      quando: '«Ricontatto, giorno: …»',
      cosaFare: 'Il ricontatto ha un dato sbagliato e l’intervento non si è salvato: correggi il campo indicato e '
        + 'salva di nuovo.',
    },
  ],
  vediAnche: ['scheda', 'consensi', 'mediazioni', 'attivita-collettive', 'agenda'],
  parole: ['presa in carico', 'colloquio', 'bozza', 'follow up', 'appuntamento', 'chiusura', 'esito', 'completa',
    'durata', 'progetto', 'finanziamento'],
}

export default capitolo
